import React, { useState, useEffect } from 'react';
import { Clock, Users, Calendar, ChevronDown, ChevronUp, Plus, Trash2, AlertCircle } from 'lucide-react';
import projectHoursService from '../services/projectHoursService';

const PROJECT_ROLES = [
    { value: 'GERENCIA', label: 'Gerência' },
    { value: 'RECEPCAO_COMPRAS_AGENDA', label: 'Recepção/Compras/Agenda' },
    { value: 'RECEPCAO_PROJETOS', label: 'Recepção de Projetos' },
    { value: 'PRODUCAO', label: 'Produção' },
    { value: 'MONTAGEM_CLIENTE', label: 'Montagem Cliente' }
];

const ProjectHoursForm = ({ projectId, onHoursChange }) => {
    const [hoursByRole, setHoursByRole] = useState([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showDetails, setShowDetails] = useState({});

    // Carregar horas existentes se o projeto já existir
    useEffect(() => {
        if (projectId) {
            loadProjectHours();
        } else {
            // Inicializar com zeros para cada cargo
            const initialHours = PROJECT_ROLES.map(role => ({
                role: role.value,
                roleLabel: role.label,
                hours: '',
                assigned_to_user_id: null,
                scheduled_start_date: '',
                scheduled_end_date: '',
                scheduled_hours_per_day: '',
                notes: ''
            }));
            setHoursByRole(initialHours);
        }
    }, [projectId]);

    const loadProjectHours = async () => {
        try {
            setLoading(true);
            const response = await projectHoursService.getProjectHours(projectId);
            const existingHours = response.data || [];

            // Mesclar com estrutura completa
            const mergedHours = PROJECT_ROLES.map(role => {
                const existing = existingHours.find(h => h.role === role.value);
                return {
                    role: role.value,
                    roleLabel: role.label,
                    hours: existing ? existing.estimated_hours : '',
                    assigned_to_user_id: existing?.assigned_to_user_id || null,
                    scheduled_start_date: existing?.scheduled_start_date || '',
                    scheduled_end_date: existing?.scheduled_end_date || '',
                    scheduled_hours_per_day: existing?.scheduled_hours_per_day || '',
                    notes: existing?.notes || '',
                    id: existing?.id || null
                };
            });
            setHoursByRole(mergedHours);
            onHoursChange(mergedHours);
        } catch (error) {
            console.error('Erro ao carregar horas do projeto:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleHoursChange = (role, field, value) => {
        const updated = hoursByRole.map(item => {
            if (item.role === role) {
                return { ...item, [field]: value };
            }
            return item;
        });
        setHoursByRole(updated);
        onHoursChange(updated);
    };

    const toggleDetails = (role) => {
        setShowDetails(prev => ({
            ...prev,
            [role]: !prev[role]
        }));
    };

    const getTotalHours = () => {
        return hoursByRole.reduce((sum, item) => sum + (parseFloat(item.hours) || 0), 0);
    };

    const getRoleLabel = (role) => {
        const found = PROJECT_ROLES.find(r => r.value === role);
        return found ? found.label : role;
    };

    return (
        <div className="bg-white border border-secondary-200 rounded-lg overflow-hidden">
            {/* Header */}
            <div
                className="bg-secondary-50 p-4 flex items-center justify-between cursor-pointer hover:bg-secondary-100 transition-colors"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center gap-3">
                    <div className="bg-primary-100 p-2 rounded-lg">
                        <Clock className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                        <h3 className="font-bold text-secondary-900">Horas por Setor</h3>
                        <p className="text-xs text-secondary-500">
                            Defina as horas estimadas para cada cargo/setor
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <span className="text-lg font-bold text-primary-600">
                            {getTotalHours().toFixed(1)}h
                        </span>
                        <span className="text-xs text-secondary-500 block">Total</span>
                    </div>
                    {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-secondary-400" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-secondary-400" />
                    )}
                </div>
            </div>

            {/* Conteúdo expandível */}
            {isExpanded && (
                <div className="p-4 space-y-4 border-t border-secondary-100">
                    {loading ? (
                        <div className="text-center py-4 text-secondary-500">
                            Carregando...
                        </div>
                    ) : (
                        <>
                            {hoursByRole.map((item) => (
                                <div key={item.role} className="border border-secondary-200 rounded-lg overflow-hidden">
                                    {/* Linha principal do cargo */}
                                    <div className="p-3 bg-secondary-50 flex items-center gap-4">
                                        <div className="flex-1">
                                            <span className="font-medium text-secondary-900">
                                                {item.roleLabel}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number"
                                                step="0.5"
                                                min="0"
                                                value={item.hours}
                                                onChange={(e) => handleHoursChange(item.role, 'hours', e.target.value)}
                                                className="input w-24 text-right"
                                                placeholder="0.0"
                                            />
                                            <span className="text-sm text-secondary-500">horas</span>
                                            <button
                                                type="button"
                                                onClick={() => toggleDetails(item.role)}
                                                className="p-1 hover:bg-secondary-200 rounded transition-colors"
                                            >
                                                {showDetails[item.role] ? (
                                                    <ChevronUp className="w-4 h-4 text-secondary-500" />
                                                ) : (
                                                    <ChevronDown className="w-4 h-4 text-secondary-500" />
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Detalhes expandíveis */}
                                    {showDetails[item.role] && (
                                        <div className="p-4 bg-white space-y-3 border-t border-secondary-100">
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <label className="text-xs text-secondary-600 block mb-1">
                                                        Data Início Agendamento
                                                    </label>
                                                    <input
                                                        type="date"
                                                        value={item.scheduled_start_date}
                                                        onChange={(e) => handleHoursChange(item.role, 'scheduled_start_date', e.target.value)}
                                                        className="input"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs text-secondary-600 block mb-1">
                                                        Data Fim Agendamento
                                                    </label>
                                                    <input
                                                        type="date"
                                                        value={item.scheduled_end_date}
                                                        onChange={(e) => handleHoursChange(item.role, 'scheduled_end_date', e.target.value)}
                                                        className="input"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-xs text-secondary-600 block mb-1">
                                                    Horas por Dia
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.5"
                                                    min="0"
                                                    max="24"
                                                    value={item.scheduled_hours_per_day}
                                                    onChange={(e) => handleHoursChange(item.role, 'scheduled_hours_per_day', e.target.value)}
                                                    className="input w-32"
                                                    placeholder="Ex: 8"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs text-secondary-600 block mb-1">
                                                    Observações
                                                </label>
                                                <textarea
                                                    value={item.notes}
                                                    onChange={(e) => handleHoursChange(item.role, 'notes', e.target.value)}
                                                    className="input"
                                                    rows="2"
                                                    placeholder="Observações sobre este setor..."
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Resumo */}
                            <div className="bg-primary-50 border border-primary-100 rounded-lg p-4 flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-primary-600 mt-0.5" />
                                <div className="text-sm text-primary-800">
                                    <strong>Resumo de Horas:</strong>
                                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                                        {hoursByRole
                                            .filter(item => parseFloat(item.hours) > 0)
                                            .map(item => (
                                                <div key={item.role} className="flex justify-between">
                                                    <span>{item.roleLabel}:</span>
                                                    <span className="font-medium">{parseFloat(item.hours).toFixed(1)}h</span>
                                                </div>
                                            ))}
                                        <div className="flex justify-between border-t border-primary-200 pt-1 mt-1">
                                            <span className="font-bold">Total:</span>
                                            <span className="font-bold">{getTotalHours().toFixed(1)}h</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProjectHoursForm;
